import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../../contexts/ThemeContext';
import { useSupplyPurchaseOrders } from '../../supplies/supply-purchase-orders/useData';
import { computePurchaseOrderTotals } from '../../supplies/supply-purchase-orders/purchaseOrderTotals';

const money = (n: number) =>
  new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

const ScmsTopSuppliersBySpend: React.FC<{ limit?: number; embedded?: boolean; settings?: Record<string, unknown> }> = ({
  limit = 5,
  embedded,
  settings,
}) => {
  const navigate = useNavigate();
  const { colors } = useTheme();
  const effectiveLimit = typeof settings?.limit === 'number' ? settings.limit : limit;
  const { data, isLoading } = useSupplyPurchaseOrders(undefined, 1, 200);

  const rows = useMemo(() => {
    const spendBySupplier = new Map<string, { name: string; amount: number }>();
    for (const po of data ?? []) {
      const supplier = po.supplier?.name || `Supplier #${po.supplierId}`;
      const current = spendBySupplier.get(supplier) ?? { name: supplier, amount: 0 };
      const totals = computePurchaseOrderTotals(po);
      current.amount += totals.grandTotalUsd ?? totals.grandTotal;
      spendBySupplier.set(supplier, current);
    }
    return Array.from(spendBySupplier.values()).sort((a, b) => b.amount - a.amount).slice(0, effectiveLimit);
  }, [data, effectiveLimit]);

  const cardClass = embedded
    ? 'min-w-0 flex-1 flex flex-col min-h-0'
    : `${colors.surface} rounded-xl shadow-sm p-5 ${colors.border} border`;

  return (
    <div className={cardClass}>
      <div className="flex items-center justify-between mb-4">
        <h2 className={`text-base font-semibold ${colors.text}`}>Supplies · Top Suppliers by Spend</h2>
        <button
          type="button"
          onClick={() => navigate('/supplies/supply-purchase-orders')}
          className={`text-sm ${colors.primaryFg} hover:opacity-80`}
        >
          View All
        </button>
      </div>
      {isLoading ? (
        <div className={`h-24 rounded-lg ${colors.surfaceHover} animate-pulse`} />
      ) : rows.length === 0 ? (
        <p className={`text-sm ${colors.textMuted}`}>No purchase order spend yet.</p>
      ) : (
        <div className="space-y-2">
          {rows.map((row) => (
            <div key={row.name} className="flex items-center justify-between rounded-lg border px-3 py-2">
              <span className={`text-sm truncate ${colors.text}`}>{row.name}</span>
              <span className={`text-sm font-semibold ${colors.text}`}>${money(row.amount)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ScmsTopSuppliersBySpend;
